/**
 * Shown on Settings → Workspace when the tenant is on a plan with a non-zero
 * `extra_bank_account_price`. Lets owners purchase additional bank-account
 * slots ($3/mo on Pro) up to the plan's hard cap.
 *
 * Pricing is per-workspace: a firm managing 25 clients will see this card on
 * each client workspace they switch into — allowing them to buy extra slots
 * for the specific client that needs them, not pay for 25 identical uplifts.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Landmark, Plus, Loader2 } from "lucide-react";

export default function BankAccountSlotsCard() {
  const { tenant } = useAuth();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  // Live price catalog — pricing updates don't need a frontend redeploy.
  useEffect(() => {
    if (!tenant?.plan) return;
    let cancelled = false;
    api.get("/billing/plans")
      .then((r) => {
        if (cancelled) return;
        const p = (r.data?.plans || []).find((x) => x.id === tenant.plan);
        if (p) setPlan(p);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenant?.plan]);

  if (!plan || (plan.extra_bank_account_price || 0) <= 0) return null;

  const current = Number(tenant?.bank_account_limit) || 0;
  const price = plan.extra_bank_account_price;
  const purchase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/bank-accounts/add", { origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout — please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="ll-card-flat p-5 mb-6"
      style={{ background: "#F0F9FF", borderColor: "#7DD3FC" }}
      data-testid="bank-account-slots-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
            <Landmark size={11} /> Bank-account slots
          </div>
          <div className="font-display font-bold text-xl mt-1">
            {current} <span className="text-[var(--ll-ink-soft)] text-base font-normal">connected in this workspace</span>
          </div>
          <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-prose">
            Add one more bank-account slot for <strong>${price}/mo</strong>. Slots are per-workspace, so if you&apos;re a firm you can top up only the clients that need more accounts.
          </p>
        </div>
        <button
          type="button"
          onClick={purchase}
          disabled={busy}
          data-testid="bank-account-slots-buy"
          className="text-xs px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60 shrink-0"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          {busy ? "Opening checkout…" : `Add one · $${price}/mo`}
        </button>
      </div>
    </div>
  );
}
